import { createHeadlessEditor } from '@lexical/headless';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import {
    $convertFromMarkdownString,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    LINK,
    ORDERED_LIST,
    STRIKETHROUGH,
    UNORDERED_LIST,
    type Transformer,
} from '@lexical/markdown';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { captureException } from '@sentry/react';
import { $getRoot, type EditorThemeClasses, type KlassConstructor, type LexicalNode } from 'lexical';
import { useEffect, useMemo, type MouseEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * An assistant reply is written in Markdown: bold, italic, strikethrough, lists
 * and links, the same set the notes editor accepts. No headings, quotes, code or
 * tables — those stay literal text if the model writes them anyway. A link to a
 * Plato page is a relative path such as `/customers/abc` (see "Page link" in
 * CONTEXT.md).
 */
const transformers: Transformer[] = [
    UNORDERED_LIST,
    ORDERED_LIST,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    STRIKETHROUGH,
    LINK,
];

const nodes: KlassConstructor<typeof LexicalNode>[] = [ListNode, ListItemNode, LinkNode];

/** A Markdown link whose target is a path inside Plato. */
const pageLinkPattern = /\[([^\]]*)\]\((\/[^)\s]*)\)/g;

/** True for an `href` that stays inside the app: a single leading slash. */
function isPageLinkHref(href: string): boolean {
    return href.startsWith('/') && !href.startsWith('//');
}

/** How many page links a reply text carries. */
export function countPageLinks(markdown: string): number {
    return [...markdown.matchAll(pageLinkPattern)].length;
}

/** Static child segments of registry routes; everything else past the first segment is an ID. */
const staticChildSegments = new Set(['info', 'insights', 'header', 'line-items']);

/**
 * The path with its entity IDs replaced by `:id`, for analytics. Every registry
 * route puts its param in the second segment (`/customers/:customer_id/info`),
 * so masking by position needs no registry on the client.
 */
export function maskPathIds(path: string): string {
    const pathname = path.split(/[?#]/)[0] ?? path;
    const segments = pathname.split('/').filter((segment) => segment.length > 0);
    const masked = segments.map((segment, index) =>
        index === 0 || staticChildSegments.has(segment) ? segment : ':id',
    );
    return `/${masked.join('/')}`;
}

/** True when the path addresses one entity rather than a list page. */
export function hasPathParams(path: string): boolean {
    return maskPathIds(path).includes(':id');
}

/**
 * The text that goes to the clipboard for one assistant bubble: the Markdown
 * rendered to plain text, with page links kept as `label (absolute URL)` so
 * they still work in an email. `origin` is the browser's, so a preview keeps
 * its host. Falls back to the rewritten Markdown if Lexical refuses the input.
 */
export function replyCopyText(markdown: string, origin: string): string {
    const withUrls = markdown.replaceAll(
        pageLinkPattern,
        (_match, label: string, path: string) => `${label} (${origin}${path})`,
    );
    try {
        const editor = createHeadlessEditor({
            nodes,
            onError: (error) => {
                throw error;
            },
        });
        editor.update(
            () => {
                $convertFromMarkdownString(withUrls, transformers, undefined, true);
            },
            { discrete: true },
        );
        // Lexical separates blocks with a blank line; the bubble shows one
        // paragraph per line break, so the copy matches what is on screen.
        return editor
            .getEditorState()
            .read(() => $getRoot().getTextContent())
            .replaceAll('\n\n', '\n');
    } catch (error) {
        captureException(error);
        return withUrls;
    }
}

/** Openers that render as raw syntax until their closer arrives. */
const pairedOpeners = ['**', '~~'];

/**
 * The part of a still-streaming text that is safe to render: an unterminated
 * link or paired mark at the tail is cut so the user never sees `[Kunden](/cu`
 * or a stray `**` snap into place a few tokens later. Only the last line is
 * inspected; earlier lines are complete paragraphs.
 */
function streamingVisibleMarkdown(markdown: string): string {
    const lastLineStart = markdown.lastIndexOf('\n') + 1;
    const lastLine = markdown.slice(lastLineStart);
    let cutAt = lastLine.length;

    const openBracket = lastUnclosedBracket(lastLine);
    if (openBracket !== undefined) {
        cutAt = Math.min(cutAt, openBracket);
    }

    for (const opener of pairedOpeners) {
        const unclosed = lastUnpairedOpener(lastLine, opener);
        if (unclosed !== undefined) {
            cutAt = Math.min(cutAt, unclosed);
        }
    }

    return markdown.slice(0, lastLineStart + cutAt);
}

/** Index of a `[` that never resolves into a complete `[label](target)`, if any. */
function lastUnclosedBracket(line: string): number | undefined {
    for (let index = line.length - 1; index >= 0; index -= 1) {
        if (line[index] !== '[') {
            continue;
        }
        const closer = line.indexOf(']', index);
        if (closer === -1) {
            return index;
        }
        // `[label]` followed by `(` needs its `)`; a bare `[label]` is finished text.
        if (line[closer + 1] === '(' && !line.slice(closer).includes(')')) {
            return index;
        }
        return undefined;
    }
    return undefined;
}

/** Index of the last `opener` that has no matching closer after it, if any. */
function lastUnpairedOpener(line: string, opener: string): number | undefined {
    const positions: number[] = [];
    for (
        let found = line.indexOf(opener);
        found !== -1;
        found = line.indexOf(opener, found + opener.length)
    ) {
        positions.push(found);
    }
    return positions.length % 2 === 1 ? positions.at(-1) : undefined;
}

/** A page link the user followed. */
export type PageLinkClick = {
    path: string;
    /** The click carried a modifier or was a middle click, so the browser opened a tab. */
    newTab: boolean;
};

export type ReplyMarkdownProps = {
    markdown: string;
    /** Still receiving deltas: an unterminated construct at the tail is held back. */
    streaming?: boolean;
    /** Called after a page link navigated. Absent on bubbles that are not replies. */
    onPageLinkClick?: (click: PageLinkClick) => void;
};

/**
 * One assistant text part, rendered from Markdown by a read-only Lexical
 * editor with the notes editor's transformers. Re-converts on every change,
 * which is how a streaming bubble grows.
 *
 * A page link navigates in place through the router so the chat keeps its
 * state; a modifier click is left to the browser. Any other URL opens a new tab.
 */
export function ReplyMarkdown({
    markdown,
    streaming = false,
    onPageLinkClick,
}: ReplyMarkdownProps): ReactElement {
    const navigate = useNavigate();
    const visible = streaming ? streamingVisibleMarkdown(markdown) : markdown;

    const theme = {
        paragraph: 'mb-cozy last:mb-0',
        text: {
            bold: 'font-semibold',
            italic: 'italic',
            strikethrough: 'line-through',
        },
        link: 'text-brand underline underline-offset-2 hover:no-underline',
        list: {
            ul: 'mb-cozy list-disc pl-default last:mb-0',
            ol: 'mb-cozy list-decimal pl-default last:mb-0',
            listitem: 'my-tight',
        },
    } satisfies EditorThemeClasses;

    // `initialConfig` is read once by the composer; the sync plugin owns updates.
    const initialConfig = useMemo(
        () => ({
            namespace: 'AskPlatoReply',
            nodes,
            editable: false,
            theme,
            onError: (error: Error) => {
                captureException(error);
            },
        }),
        [],
    );

    const followLink = (event: MouseEvent<HTMLDivElement>): void => {
        if (!(event.target instanceof Element)) {
            return;
        }
        const anchor = event.target.closest('a');
        const href = anchor?.getAttribute('href');
        if (!anchor || !href) {
            return;
        }
        if (!isPageLinkHref(href)) {
            event.preventDefault();
            window.open(href, '_blank', 'noopener,noreferrer');
            return;
        }
        const newTab = event.button === 1 || event.metaKey || event.ctrlKey || event.shiftKey;
        if (!newTab) {
            event.preventDefault();
            void navigate(href);
        }
        onPageLinkClick?.({ path: href, newTab });
    };

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <div onClick={followLink} onAuxClick={followLink}>
                <RichTextPlugin
                    contentEditable={<ContentEditable className="outline-hidden" />}
                    ErrorBoundary={LexicalErrorBoundary}
                />
            </div>
            <ListPlugin />
            <LinkPlugin />
            <MarkdownSyncPlugin markdown={visible} />
        </LexicalComposer>
    );
}

/** Replaces the editor content with the converted Markdown whenever it changes. */
function MarkdownSyncPlugin({ markdown }: { markdown: string }): null {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        editor.update(() => {
            $convertFromMarkdownString(markdown, transformers, undefined, true);
        });
    }, [editor, markdown]);

    return null;
}
